import { SortKey, getSortKeyBySortSlug, sortSlugSchema } from '@/resource/ad';
import * as validators from '@/util/validators';
import z from 'zod';

const distanceValidator = validators.numeric({ min: 1, max: 3 });
const postalCodeValidator = validators.numeric({ min: 4, max: 4 });
const pageValidator = validators.numeric({ min: 1, max: 4 });
const categoryIdsValidator = z
  .string()
  .max(512)
  .regex(/^(\d{1,4}\.?){1,50}$/);
const searchValidator = z
  .string()
  .max(128)
  .regex(/^[a-zA-Z0-9_.-]{1,128}$/);
export const filterPairsValidator = z.string().array().max(20);

type FilterTuple =
  | ['page', number | undefined]
  | ['distance', number | undefined]
  | ['postal_code', number | undefined]
  | ['category_ids', number[] | undefined]
  | ['keyword', string | undefined]
  | ['sort', SortKey | undefined];

function parseFilter(key: string, value: string): FilterTuple | null {
  switch (key) {
    case 'afstand': {
      const parsed = distanceValidator.safeParse(value);
      if (parsed.success) {
        return ['distance', parsed.data];
      }

      return null;
    }

    case 'vanaf': {
      const parsed = postalCodeValidator.safeParse(value);
      if (parsed.success) {
        return ['postal_code', parsed.data];
      }

      return null;
    }

    case 'categorie': {
      const parsed = categoryIdsValidator.safeParse(value);
      if (parsed.success) {
        const ids = parsed.data
          .split('.')
          .map(Number)
          .filter((x) => x);
        return ['category_ids', ids];
      }

      return null;
    }

    case 'meest': {
      const parsed = sortSlugSchema.safeParse(value);

      if (parsed.success) {
        return ['sort', getSortKeyBySortSlug(parsed.data) || 'recent'];
      }

      return null;
    }

    case 'zoek': {
      const parsed = searchValidator.safeParse(value);
      if (parsed.success) {
        return ['keyword', parsed.data];
      }

      return null;
    }

    case 'p': {
      const parsed = pageValidator.safeParse(value);
      if (parsed.success) {
        return ['page', parsed.data];
      }

      return null;
    }
  }

  return null;
}

export function parseFilters(pairs: string[]): FilterTuple[] | null {
  if (pairs.length % 2 !== 0) {
    return null;
  }

  const filters: FilterTuple[] = [];
  for (let i = 0; i < pairs.length; i += 2) {
    const key = pairs[i];
    const value = pairs[i + 1];
    const parsed = parseFilter(key, value);

    if (Array.isArray(parsed)) {
      filters.push(parsed);
    } else {
      return null;
    }
  }

  return filters;
}
